import { Button } from "@/components/ui/button";
import { Wallet, AlertTriangle } from "lucide-react";
import { useAccount, useDisconnect, useNetwork, useSwitchNetwork } from 'wagmi';
import { formatAddress } from "@/lib/utils";
import { useWeb3Modal } from '@web3modal/wagmi/react';
import { useEffect, useState, useCallback } from 'react';
import { useAnalytics } from '@/hooks/useAnalytics';
import { toast } from "sonner";
import { logEnvironmentVariables, logger } from '../../utils/logger';

// Detect if we're on a mobile device
const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

// Restore full chain support
const SUPPORTED_CHAINS = [
  56,       // BSC Mainnet
  1,        // ETH Mainnet
  43114,    // Avalanche Mainnet
  97,       // BSC Testnet
  11155111, // Sepolia
  43113,    // Avalanche Fuji
  2741,     // Abstract Mainnet
  11124,    // Abstract Testnet
  8453,     // Base Mainnet
  84532,    // Base Sepolia
  10,       // OP Mainnet
  11155420, // OP Sepolia
  42161,    // Arbitrum One
  421614,   // Arbitrum Sepolia
  137,      // Polygon Mainnet
  80002,    // Polygon Amoy
  1329,     // Sei Mainnet
  1328,     // Sei Atlantic-2 Testnet
  204,      // opBNB Mainnet
  5611,     // opBNB Testnet
];

// Network metadata with chainIds
const NETWORK_METADATA = {
  1: {
    name: 'Ethereum',
    chainId: '0x1'
  },
  56: {
    name: 'BNB Smart Chain',
    chainId: '0x38'
  },
  43114: {
    name: 'Avalanche',
    chainId: '0xa86a'
  },
  97: {
    name: 'BSC Testnet',
    chainId: '0x61'
  },
  11155111: {
    name: 'Sepolia',
    chainId: '0xaa36a7'
  },
  43113: {
    name: 'Avalanche Fuji',
    chainId: '0xa869'
  },
  2741: {
    name: 'Abstract',
    chainId: '0xab5'
  },
  11124: {
    name: 'Abstract Sepolia',
    chainId: '0x2b74'
  },
  8453: {
    name: 'Base',
    chainId: '0x2105'
  },
  84532: {
    name: 'Base Sepolia',
    chainId: '0x14a34'
  },
  10: {
    name: 'OP Mainnet',
    chainId: '0xa'
  },
  11155420: {
    name: 'OP Sepolia',
    chainId: '0xaa37dc'
  },
  42161: {
    name: 'Arbitrum One',
    chainId: '0xa4b1'
  },
  421614: {
    name: 'Arbitrum Sepolia',
    chainId: '0x66eee'
  },
  137: {
    name: 'Polygon',
    chainId: '0x89'
  },
  80002: {
    name: 'Polygon Amoy',
    chainId: '0x13882'
  },
  204: {
    name: 'opBNB Mainnet',
    chainId: '0xcc'
  },
  5611: {
    name: 'opBNB Testnet',
    chainId: '0x15eb'
  },
};

export function WalletButton() {
  const { isConnected, address } = useAccount();
  const { disconnect } = useDisconnect();
  const { open } = useWeb3Modal();
  const { chain } = useNetwork();
  const { switchNetwork, error: switchError, isLoading: isSwitchingNetwork } = useSwitchNetwork();
  const { trackWalletConnect, trackWalletDisconnect } = useAnalytics();
  const [isUnsupportedNetwork, setIsUnsupportedNetwork] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  const [preferredChainId, setPreferredChainId] = useState<number | undefined>(56); // Default to BSC
  const [isShimmering, setIsShimmering] = useState(false);

  // Add shimmer animation keyframes
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes shimmer {
        0% {
          left: -100%;
        }
        100% {
          left: 100%;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Helper function to trigger shimmer effect
  const triggerShimmer = () => {
    setIsShimmering(true);
    setTimeout(() => setIsShimmering(false), 800);
  };

  // Log environment variables on component mount
  useEffect(() => {
    logEnvironmentVariables();
  }, []);

  // Check for supported networks
  useEffect(() => {
    if (chain && isConnected) {
      const isSupported = SUPPORTED_CHAINS.includes(chain.id);
      setIsUnsupportedNetwork(!isSupported);
      
      if (!isSupported) {
        toast.error('Please switch to a supported network');
        logger.warn('Current chain is not supported:', chain.id);
      } else {
        // If we're on a supported chain, update our preferred chain
        setPreferredChainId(chain.id);
        logger.debug('Connected to supported chain:', chain.id);
      }
    } else {
      setIsUnsupportedNetwork(false);
    }
  }, [chain, isConnected]);

  // Monitor switch network errors
  useEffect(() => {
    if (switchError) {
      logger.error('Network switch error:', switchError);
      toast.error('Failed to switch network. Please try manually in your wallet.');
    }
  }, [switchError]);

  // Reset connection state when connected
  useEffect(() => {
    if (isConnected) {
      setIsConnecting(false);
      setConnectionAttempts(0);
    }
  }, [isConnected]);

  // Enhanced connect handler with mobile-specific logic
  const handleConnect = async () => {
    try {
      setIsConnecting(true);
      setConnectionAttempts(prev => prev + 1);
      
      // Use the same approach for both mobile and desktop
      logger.info('Wallet connection attempt');
      
      // Connect directly without showing network selection first
      try {
        await open({ view: 'Connect' });
        trackWalletConnect('web3modal');
      } catch (error) {
        logger.warn('Connection method failed', error);
        throw error;
      }
    } catch (error) {
      logger.error('Connection error:', error);
      
      // Different error messages based on connection attempts
      if (connectionAttempts > 2) {
        toast.error('Connection issues detected. Try opening your wallet app directly and connect from there.');
      } else {
        toast.error('Please try connecting through your wallet app');
      }
    } finally {
      // Reset connecting state after a delay
      setTimeout(() => {
        setIsConnecting(false);
      }, 1000);
    }
  };

  const handleDisconnect = useCallback(async () => {
    try {
      await disconnect();
      trackWalletDisconnect();
    } catch (error) {
      logger.error('Disconnect error:', error);
      toast.error('Please disconnect through your wallet app');
    }
  }, [disconnect, trackWalletDisconnect]);

  // Enhanced network switching function that uses both wagmi and web3modal
  const handleSwitchNetwork = useCallback(async () => {
    logger.info('Attempting to switch network...');
    
    // Default to BSC if no preferred chain
    const targetChainId = preferredChainId || 56;
    
    try {
      // Check if we're using WalletConnect (QR code connection)
      const isWalletConnectConnection = localStorage.getItem('wagmi.wallet') === 'walletConnect';
      logger.info('Connection type:', isWalletConnectConnection ? 'WalletConnect' : 'Direct/Injected');
      
      // For WalletConnect connections, use only wagmi's switchNetwork
      if (isWalletConnectConnection) {
        if (switchNetwork) {
          logger.info('Using wagmi switchNetwork for WalletConnect connection to', targetChainId);
          
          // Store network change info for potential refresh handling
          localStorage.setItem('lastNetworkChange', JSON.stringify({
            timestamp: Date.now(),
            fromChainId: chain?.id,
            toChainId: targetChainId
          }));
          
          switchNetwork(targetChainId);
          return;
        } else {
          throw new Error("No switchNetwork method available for WalletConnect");
        }
      }
      
      // Special handling for Sepolia network
      if (targetChainId === 11155111) {
        logger.info('Special handling for Sepolia network');
        
        // Log all available RPC URLs for debugging
        const sepoliaRpcs = [
          import.meta.env.VITE_SEPOLIA_RPC,
          import.meta.env.VITE_ETH_TESTNET_RPC,
          'https://rpc.sepolia.org',
          'https://ethereum-sepolia.publicnode.com',
          'https://sepolia.infura.io/v3/9aa3d95b3bc440fa88ea12eaa4456161', // Public Infura endpoint
          'https://rpc2.sepolia.org',
        ].filter(Boolean);
        
        logger.info('Available Sepolia RPCs:', sepoliaRpcs);
        
        // Try direct method for Sepolia using window.ethereum
        if (window.ethereum) {
          try {
            logger.info('Requesting Sepolia chain switch via ethereum provider');
            
            // First try to switch to the existing chain
            try {
              await window.ethereum.request({
                method: 'wallet_switchEthereumChain',
                params: [{ chainId: '0xaa36a7' }], // Sepolia chainId in hex
              });
              
              logger.info('Successfully switched to Sepolia');
              
              // Force a page reload after a short delay to ensure UI updates
              setTimeout(() => {
                logger.info('Reloading page to reflect Sepolia network change');
                window.location.reload();
              }, 1000);
              
              return;
            } catch (switchError: any) {
              // If chain doesn't exist in wallet, try adding it
              if (switchError.code === 4902) {
                logger.info('Sepolia not found in wallet, attempting to add it');
                
                // Try multiple RPC URLs in sequence if one fails
                for (let i = 0; i < sepoliaRpcs.length; i++) {
                  try {
                    const rpcUrl = sepoliaRpcs[i];
                    logger.info(`Trying to add Sepolia with RPC URL: ${rpcUrl}`);
                    
                    await window.ethereum.request({
                      method: 'wallet_addEthereumChain',
                      params: [
                        {
                          chainId: '0xaa36a7',
                          chainName: 'Sepolia',
                          nativeCurrency: {
                            name: 'Sepolia ETH',
                            symbol: 'ETH',
                            decimals: 18,
                          },
                          rpcUrls: [rpcUrl],
                          blockExplorerUrls: ['https://sepolia.etherscan.io'],
                        },
                      ],
                    });
                    
                    logger.info('Successfully added Sepolia with RPC:', rpcUrl);
                    
                    // Try switching again after adding
                    await window.ethereum.request({
                      method: 'wallet_switchEthereumChain',
                      params: [{ chainId: '0xaa36a7' }],
                    });
                    
                    logger.info('Successfully switched to newly added Sepolia');
                    
                    // Force a page reload after a short delay to ensure UI updates
                    setTimeout(() => {
                      logger.info('Reloading page to reflect Sepolia network change');
                      window.location.reload();
                    }, 1000);
                    
                    return;
                  } catch (addError) {
                    logger.error(`Failed to add Sepolia with RPC ${sepoliaRpcs[i]}:`, addError);
                    // Continue to next RPC URL if this one failed
                  }
                }
                
                // If we've tried all RPCs and none worked, show an error
                toast.error('Failed to add Sepolia network. Please add it manually in your wallet.');
              } else {
                logger.error('Error switching to Sepolia:', switchError);
                toast.error('Failed to switch to Sepolia. Please try manually in your wallet.');
              }
            }
          } catch (sepoliaError) {
            logger.error('Sepolia direct switch error:', sepoliaError);
            toast.error('Error connecting to Sepolia. Please try manually in your wallet settings.');
          }
        } else {
          logger.info('No injected ethereum provider found, using fallback');
          // Fallback to wagmi or web3modal
          if (switchNetwork) {
            switchNetwork(11155111);
          } else {
            await open({ view: 'Networks' });
          }
        }
        return;
      }
      
      // Standard approach for other networks
      // First try using wagmi's switchNetwork (works better with injected wallets)
      if (switchNetwork) {
        logger.info('Using wagmi switchNetwork to', targetChainId);
        switchNetwork(targetChainId);
        return;
      }
      
      // Fallback to web3modal if wagmi's switchNetwork is not available
      logger.info('Fallback: Using web3modal to switch network');
      await open({ view: 'Networks' });
    } catch (error) {
      logger.error('Error switching network:', error);
      toast.error('Network switching failed. Please try manually in your wallet app.');
      
      // For mobile, provide more helpful guidance
      if (isMobile) {
        toast.error('Try opening your wallet app directly and switching networks there.');
      }
    }
  }, [switchNetwork, open, preferredChainId]);

  if (!isConnected) {
    return (
      <Button
        variant="ghost"
        className="inline-flex items-center justify-center h-9 gap-2 px-3 hover:bg-accent rounded-md transition-colors border-2 group touch-manipulation relative overflow-hidden"
        style={{ borderColor: 'var(--color-header-accent, #0A74FF)' }}
        onClick={() => {
          triggerShimmer();
          handleConnect();
        }}
        disabled={isConnecting}
      >
        <Wallet className="h-4 w-4 relative z-10" style={{ color: 'var(--color-header-accent, #0A74FF)' }} />
        <span className="text-sm relative z-10">{isConnecting ? 'Connecting...' : 'Connect Wallet'}</span>
        {isShimmering && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, transparent, rgba(10, 116, 255, 0.6), transparent)',
              animation: 'shimmer 0.8s ease-in-out',
              pointerEvents: 'none',
              zIndex: 1
            }}
          />
        )}
      </Button>
    );
  }

  if (isUnsupportedNetwork) {
    return (
      <Button
        variant="ghost"
        className="inline-flex items-center justify-center h-9 gap-2 px-3 hover:bg-accent rounded-md transition-colors border-2 group touch-manipulation"
        style={{ borderColor: 'var(--color-header-accent, #0A74FF)' }}
        onClick={handleSwitchNetwork}
        disabled={isSwitchingNetwork}
      >
        <AlertTriangle className="h-4 w-4 text-red-500 group-hover:text-red-400" />
        <span className="text-sm text-red-500">
          {isSwitchingNetwork ? 'Switching...' : 'Switch Network'}
        </span>
      </Button>
    );
  }

  return (
    <Button
      variant="ghost"
      className="inline-flex items-center justify-center h-9 gap-2 px-3 hover:bg-accent rounded-md transition-colors border-2 group touch-manipulation"
      style={{ borderColor: 'var(--color-header-accent, #0A74FF)' }}
      onClick={handleDisconnect}
    >
      <Wallet className="h-4 w-4" style={{ color: 'var(--color-header-accent, #0A74FF)' }} />
      <span className="text-sm">{formatAddress(address)}</span>
    </Button>
  );
}
